import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Table, Typography, Divider, Button, Spin, message } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import * as invoiceApi from '../../services/invoiceApi';

const { Title, Text } = Typography;

const InvoiceView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInvoice = async () => {
      setLoading(true);
      try {
        const res = await invoiceApi.getInvoice(id);
        setInvoice(res.data);
      } catch (err) {
        message.error('Failed to fetch invoice');
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [id]);

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>;
  if (!invoice) return <div style={{ textAlign: 'center', padding: 40 }}>Invoice not found</div>;

  // If jobs are not included in the invoice, you may want to fetch and display them separately
  // For now, we just show the summary fields

  return (
    <Card style={{ maxWidth: 900, margin: 'auto', marginTop: 32 }}>
      <Row>
        <Col span={12}>
          <Title level={2}>TAX INVOICE</Title>
          <Text strong>{invoice.customerName}</Text>
        </Col>
        <Col span={12} style={{ textAlign: 'right' }}>
          <Text>Invoice Date: {invoice.invoiceDate?.slice(0, 10)}</Text><br />
          <Text>Invoice Number: {invoice.invoiceNumber}</Text><br />
          {invoice.orderNo && <><Text>Order No: {invoice.orderNo}</Text><br /></>}
          <Text>VAT Number: {invoice.vatNumber}</Text><br />
          {invoice.channel && <Text>Channel: {invoice.channel}</Text>}
        </Col>
      </Row>
      <Divider />
      {/* Jobs and Extra Charges Table */}
      <Table
        columns={[
          { title: 'Description', dataIndex: 'description', key: 'description' },
          { title: 'Customer', dataIndex: 'customer', key: 'customer' },
          { title: 'Quantity', dataIndex: 'quantity', key: 'quantity', align: 'right' },
          { title: 'Unit Price', dataIndex: 'unitPrice', key: 'unitPrice', align: 'right' },
          { title: 'VAT', dataIndex: 'vat', key: 'vat', align: 'right' },
          { title: 'Amount', dataIndex: 'amount', key: 'amount', align: 'right' },
        ]}
        dataSource={[
          ...(invoice.jobIds || []).map((job, index) => {
            // Get customer name from jobCustomers if available, otherwise from job.customer
            let customerName = 'Unknown Customer';
            if (invoice.jobCustomers && invoice.jobCustomers[index]) {
              customerName = invoice.jobCustomers[index].customerName;
            } else if (job.customer && job.customer.name) {
              customerName = job.customer.name;
            }
            
            return {
              key: job._id,
              description: job.bookingReference || job.reference,
              customer: customerName,
              quantity: 1,
              unitPrice: job.payment?.amount ? `£${job.payment.amount.toFixed(2)}` : 'N/A',
              vat: job.payment?.vatRate !== undefined && job.payment?.vatRate !== null ? `${job.payment.vatRate}%` : '20%',
              amount: job.payment?.totalAmount ? `£${job.payment.totalAmount.toFixed(2)}` : 'N/A',
            };
          }),
          ...((invoice.extraCharges || []).map((charge, idx) => {
            const chargeVat = +(Number(charge.amount) * Number(charge.vat) / 100).toFixed(2);
            return {
              key: `extra-${idx}`,
              description: charge.name,
              customer: 'N/A',
              quantity: 1,
              unitPrice: `£${Number(charge.amount).toFixed(2)}`,
              vat: `${Number(charge.vat)}%`,
              amount: `£${(Number(charge.amount) + chargeVat).toFixed(2)}`,
            };
          }))
        ]}
        pagination={false}
        summary={() => (
          <>
            <Table.Summary.Row>
              <Table.Summary.Cell colSpan={5} align="right">Subtotal</Table.Summary.Cell>
              <Table.Summary.Cell align="right">£{invoice.subtotal?.toFixed(2) || '0.00'}</Table.Summary.Cell>
            </Table.Summary.Row>
            <Table.Summary.Row>
              <Table.Summary.Cell colSpan={5} align="right">TOTAL VAT</Table.Summary.Cell>
              <Table.Summary.Cell align="right">£{invoice.vat?.toFixed(2) || '0.00'}</Table.Summary.Cell>
            </Table.Summary.Row>
            <Table.Summary.Row>
              <Table.Summary.Cell colSpan={5} />
              <Table.Summary.Cell
                align="right"
                style={{
                  borderTop: '3px solid #222',
                  fontWeight: 'bold',
                  fontSize: 16,
                  paddingTop: 8
                }}
              />
            </Table.Summary.Row>
            <Table.Summary.Row>
              <Table.Summary.Cell colSpan={5} align="right" style={{ fontWeight: 'bold', fontSize: 16 }}>
                <b style={{ fontSize: 16 }}>TOTAL GBP</b>
              </Table.Summary.Cell>
              <Table.Summary.Cell align="right" style={{ fontWeight: 'bold', fontSize: 16 }}>
                <b style={{ fontSize: 16 }}>£{invoice.total?.toFixed(2) || '0.00'}</b>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </>
        )}
      />
      <Divider />
      <Row>
        <Col span={12}>
          <Text strong>Due Date: {invoice.dueDate?.slice(0, 10)}</Text><br />
          {invoice.bankDetails && <>
            <Text>Account Name: {invoice.bankDetails.accountName}</Text><br />
            <Text>Account Number: {invoice.bankDetails.accountNumber}</Text><br />
            <Text>Sort Code: {invoice.bankDetails.sortCode}</Text><br />
            <Text>VAT Number: {invoice.bankDetails.vatNumber}</Text>
          </>}
        </Col>
      </Row>
      <Divider />
      {/* Add a bold, dashed line with scissors icon before PAYMENT ADVICE */}
      <div style={{ display: 'flex', alignItems: 'center', margin: '24px 0' }}>
        {/* SVG scissors icon, rotated to face the line */}
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: 8 }}>
          <path d="M9.5 8.5L19 19M9.5 15.5L19 6M6 6.5C7.10457 6.5 8 7.39543 8 8.5C8 9.60457 7.10457 10.5 6 10.5C4.89543 10.5 4 9.60457 4 8.5C4 7.39543 4.89543 6.5 6 6.5ZM6 13.5C7.10457 13.5 8 14.3954 8 15.5C8 16.6046 7.10457 17.5 6 17.5C4.89543 17.5 4 16.6046 4 15.5C4 14.3954 4.89543 13.5 6 13.5Z" stroke="#222" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <div style={{ flex: 1, borderTop: '3px dashed #222', height: 0 }} />
      </div>
      <Title level={4}>PAYMENT ADVICE</Title>
      <Row>
        <Col span={12}>
          {invoice.bankDetails && <>
            <Text>To: {invoice.bankDetails.accountName}</Text><br />
            <Text>Account Number: {invoice.bankDetails.accountNumber}</Text><br />
            <Text>Sort Code: {invoice.bankDetails.sortCode}</Text><br />
          </>}
        </Col>
        <Col span={12} style={{ textAlign: 'right' }}>
          <Text>Customer: {invoice.customerName}</Text><br />
          <Text>Invoice Number: {invoice.invoiceNumber}</Text><br />
          <Text>Amount Due: <b>{invoice.total?.toFixed(2)}</b></Text><br />
          <Text>Due Date: {invoice.dueDate?.slice(0, 10)}</Text><br />
        </Col>
      </Row>
      <Divider />
      <Button 
        onClick={async () => {
          try {
            const response = await invoiceApi.downloadInvoicePDF(invoice._id);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `invoice-${invoice.invoiceNumber}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            message.success('Invoice downloaded successfully');
          } catch (error) {
            console.error('Error downloading invoice:', error);
            message.error('Failed to download invoice');
          }
        }} 
        type="primary"
      >
        Download PDF
      </Button>
      <Button style={{ marginLeft: 8 }} onClick={() => navigate('/invoices')}>Back to List</Button>
    </Card>
  );
};

export default InvoiceView; 